<template>
  <div class="drawing-container">
    <!-- Info Card -->
    <div class="info-card">
      <div class="info-item">
        <span class="info-label">{{ $t("project.drawing.areaSize") }}</span>
        <span class="info-value">{{ canvasSizeInMeters }} m</span>
      </div>

      <div class="info-item">
        <span class="info-label">{{ $t("project.drawing.totalRoof") }}</span>
        <span class="info-value">{{ totalRoofAreaInMeters }} m²</span>
      </div>
      <div class="info-item">
        <span class="info-label">{{ $t("project.drawing.panels") }}</span>
        <span class="info-value">{{ panels.length }}</span>
      </div>
    </div>

    <!-- Drawing Controls (Buttons from MapComponent.vue) -->
    <div class="drawing-controls">
      <button
        class="control-button"
        :class="{ active: currentTool === 'move' }"
        :title="$t('project.drawing.controls.moveMap')"
        @click="setTool('move')"
      >
        <i class="bi bi-arrows-move"></i>
      </button>
      <button
        class="control-button"
        :class="{ active: currentTool === 'roof' }"
        :title="$t('project.drawing.controls.drawRoof')"
        @click="setTool('roof')"
      >
        <i class="bi bi-bounding-box"></i>
      </button>
      <button
        class="control-button"
        :class="{ active: currentTool === 'delete' }"
        :title="$t('project.drawing.controls.deleteShape')"
        @click="setTool('delete')"
      >
        <i class="bi bi-eraser"></i>
      </button>

      <button
        class="control-button"
        :class="{ active: currentTool === 'obstacle' }"
        :title="$t('project.drawing.controls.drawObstacle')"
        @click="setTool('obstacle')"
      >
        <i class="bi bi-pin-angle"></i>
      </button>
      <button
        class="control-button"
        :class="{ active: currentTool === 'slope' }"
        :title="$t('project.drawing.controls.insertSlope')"
        @click="setTool('slope')"
      >
        <img
          v-if="slopeIcon"
          :src="slopeIcon.src"
          alt="Slope"
          class="slope-icon"
        />
        <i v-else class="bi bi-compass"></i>
      </button>
      <button
        class="control-button"
        :class="{ active: currentTool === 'addPanel' }"
        :title="
          currentTool === 'addPanel'
            ? $t('project.drawing.controls.toggleOrientation')
            : $t('project.drawing.controls.addPanel')
        "
        @click="handlePanelButtonClick"
      >
        <i
          class="bi bi-file-plus"
          :style="{ transform: isVerticalPanel ? 'rotate(90deg)' : 'none' }"
        ></i>
      </button>
      <button
        class="control-button"
        :class="{ active: currentTool === 'rotate' }"
        :title="$t('project.drawing.controls.rotateCanvas')"
        @click="setTool('rotate')"
      >
        <i class="bi bi bi-hr"></i>
      </button>
    </div>

    <!-- Clear All Button (Bottom Left) -->
    <div class="clear-all-button">
      <button
        class="control-button"
        :title="$t('project.drawing.controls.undoDelete')"
        @click="undoDelete"
        :disabled="!canUndo"
      >
        <i class="bi bi-arrow-90deg-left"></i>
      </button>
      <button
        class="control-button"
        :title="$t('project.drawing.controls.clearAll')"
        @click="showClearModal = true"
      >
        <i class="bi bi-trash"></i>
      </button>
    </div>

    <!-- Clear Canvas Confirmation Modal -->
    <div
      v-if="showClearModal"
      class="modal-overlay"
      @click="showClearModal = false"
    >
      <div class="modal-content" @click.stop>
        <h3>{{ $t("project.drawing.clearModal.title") }}</h3>
        <p>{{ $t("project.drawing.clearModal.message") }}</p>
        <div class="d-flex gap-2">
          <button @click="showClearModal = false" class="btn btn-secondary">
            {{ $t("project.drawing.clearModal.cancel") }}
          </button>
          <button @click="confirmClearCanvas" class="btn btn-danger">
            {{ $t("project.drawing.clearModal.clear") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Shape Confirmation Modal -->
    <div
      v-if="showDeleteShapeModal"
      class="modal-overlay"
      @click="cancelDeleteShape"
    >
      <div class="modal-content" @click.stop>
        <h3>{{ $t("project.drawing.deleteShapeModal.title") }}</h3>
        <p>{{ $t("project.drawing.deleteShapeModal.message") }}</p>
        <div class="d-flex gap-2">
          <button @click="cancelDeleteShape" class="btn btn-secondary">
            {{ $t("project.drawing.deleteShapeModal.cancel") }}
          </button>
          <button @click="confirmDeleteShape" class="btn btn-danger">
            {{ $t("project.drawing.deleteShapeModal.delete") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Roof Confirmation Modal -->
    <div
      v-if="showDeleteRoofModal"
      class="modal-overlay"
      @click="cancelDeleteRoof"
    >
      <div class="modal-content" @click.stop>
        <h3>{{ $t("project.drawing.clearModal.title") }}</h3>
        <p>{{ $t("project.drawing.clearModal.message") }}</p>
        <div class="d-flex gap-2">
          <button @click="cancelDeleteRoof" class="btn btn-secondary">
            {{ $t("project.drawing.clearModal.cancel") }}
          </button>
          <button @click="confirmDeleteRoof" class="btn btn-danger">
            {{ $t("project.drawing.clearModal.clear") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Drawing Canvas -->
    <canvas
      id="drawingCanvas"
      :class="[
        'drawing-canvas',
        {
          'move-tool': currentTool === 'move',
          'delete-tool': currentTool === 'delete',
          'baseline-select': selectingBaseLine,
          'panel-tool': currentTool === 'addPanel',
          dragging: isPanning,
          'multi-select-mode': isMultiSelectMode && currentTool === 'move',
          'slope-tool': currentTool === 'slope',
        },
      ]"
      @mousedown="handleCanvasMouseDown"
      @mousemove="handleCanvasMouseMove"
      @mouseup="handleCanvasMouseUp"
      @wheel="handleCanvasWheel"
      ref="canvas"
    ></canvas>

    <!-- Instructions overlay -->
    <div
      v-if="(currentTool === 'roof' || currentTool === 'obstacle') && isDrawing"
      class="instructions-overlay"
    >
      {{ $t("project.drawing.instructions") }}
    </div>

    <!-- Panel Selection Notification -->
    <div v-if="showPanelNotification" class="panel-notification">
      <div class="panel-notification-content">
        <i class="bi bi-exclamation-circle"></i>
        <span>{{
          showPanelNotification === "limit"
            ? $t("project.drawing.maxPanelsReached")
            : $t("project.drawing.selectPanelFirst")
        }}</span>
      </div>
    </div>

    <!-- Baseline Selection Notification -->
    <div v-if="showBaselineNotification" class="panel-notification">
      <div class="panel-notification-content">
        <i class="bi bi-exclamation-circle"></i>
        <span>{{ $t("project.drawing.selectBaselineFirst") }}</span>
      </div>
    </div>

    <div v-if="showCaptureNotification" class="panel-notification success">
      <div class="panel-notification-content">
        <i class="bi bi-check-circle"></i>
        <span>{{ $t("project.drawing.drawingCaptured") }}</span>
      </div>
    </div>

    <!-- Slope Popup -->
    <div v-if="showSlopePopup" class="slope-popup">
      <div class="slope-popup-header">
        {{ $t("project.drawing.insertSlope") }}
      </div>
      <div class="slope-popup-content">
        <div class="slope-input-group">
          <label for="slopeRange"
            >{{ $t("project.drawing.slopeRange") }} {{ slopeValue }}°</label
          >
          <input
            type="range"
            id="slopeRange"
            v-model="slopeValue"
            min="0"
            max="90"
            step="1"
            class="slope-range"
          />
        </div>
        <div class="slope-buttons">
          <button
            v-if="!selectingBaseLine && selectedLineForSlope === -1"
            @click="startSelectingBaseLine"
            class="slope-button"
          >
            {{ $t("project.drawing.selectBaseLine") }}
          </button>
          <button
            v-if="selectedLineForSlope !== -1"
            @click="applySlope"
            class="slope-button apply-button"
          >
            {{ $t("project.drawing.apply") }}
          </button>
          <button @click="cancelSlope" class="slope-button cancel-button">
            {{ $t("project.drawing.cancel") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Zoom controls -->
    <div v-if="currentTool === 'move'" class="zoom-controls">
      <button @click="zoomIn" class="zoom-button">
        <i class="bi bi-plus"></i>
      </button>
      <button @click="zoomOut" class="zoom-button">
        <i class="bi bi-dash"></i>
      </button>
    </div>

    <!-- Scale indicator - moved outside zoom controls -->
    <div v-if="currentTool === 'move'" class="scale-indicator">
      <div class="scale-label">
        {{ gridRealSize.toFixed(1) }}m × {{ gridRealSize.toFixed(1) }}m
        {{ $t("project.drawing.perGrid") }}
      </div>
    </div>

    <!-- Multi-select mode indicator -->
    <div v-if="isMultiSelectMode" class="multi-select-indicator">
      {{ $t("project.drawing.multiSelectMode") }}
    </div>

    <!-- Compass for rotation -->
    <div
      class="compass-container"
      @click="resetCanvasOrientation"
      :title="$t('project.drawing.resetRotation')"
    >
      <div
        class="compass"
        :style="{ transform: `rotate(${canvasRotation}rad)` }"
      >
        <i class="bi bi-arrow-up-circle-fill"></i>
        <div class="compass-north">N</div>
      </div>
    </div>
  </div>
</template>

<script>
import { useEquipmentStore } from "@/stores/equipment";
import { useMapStateStore } from "@/stores/mapState";
import { useProjectStore } from "@/stores/project";
import {
  calculateRealWorldScale,
  calculateCanvasSizeInMeters,
  calculateTotalRoofAreaInMeters,
  calculateLineLength,
  captureDrawingScreenshot,
} from "@/utils/drawingUtils";
import { getPanelImageUrl } from "../utils/panelImageUtils";

export default {
  name: "DrawingComponent",
  props: {
    selectedPanelModel: {
      type: String,
      default: null,
    },
    totalPanels: {
      type: Number,
      default: 0,
    },
  },
  emits: ["update:drawingData"],
  data() {
    return {
      ctx: null,
      currentTool: null,
      isDrawing: false,
      currentPolygon: [],
      polygons: [],
      obstacles: [],
      parentShapeIndex: -1,
      canvasWidth: 0,
      canvasHeight: 0,
      snapDistance: 20,
      tempPoint: null,
      closePath: false,
      selectedPolygonIndex: -1,
      selectedPointIndex: -1,
      isDragging: false,
      hoverPointInfo: null,
      // Slope related properties
      showSlopePopup: false,
      slopeValue: 16, // Default slope value
      selectingBaseLine: false,
      selectedPolygonForSlope: -1,
      selectedLineForSlope: -1, // [startPointIndex, endPointIndex]
      // Rotation related properties
      canvasRotation: 0,
      selectedPolygonForRotation: -1,
      // Pan and zoom related properties
      viewportTransform: {
        x: 0,
        y: 0,
        zoom: 1,
      },
      isPanning: false,
      lastPanPoint: null,
      zoom: 1,
      zoomStep: 0.1,
      minZoom: 1,
      maxZoom: 10,
      realWorldScale: 5,
      gridSize: 50, // Pixel size of the grid
      gridRealSize: 10, // Real-world size in meters (10m×10m as displayed in UI)
      // Background image
      backgroundImage: null,
      // Add new properties for panel functionality
      panels: [],
      showPanelNotification: false, // Can be false, true, or 'limit'
      isVerticalPanel: false,
      selectedPanelIndex: -1,
      isDraggingPanel: false,



      selectedPanels: [], // Array to track selected panel indices
      isMultiSelectMode: false, // Whether multi-select mode is active (Ctrl key pressed)
      deleteHistory: [], // Array to store deleted items
      maxHistorySize: 10, // Maximum number of items to keep in history
      showCaptureNotification: false,
      // Add panel image
      panelImage: null,
      // Cache for dynamically loaded panel images based on color
      panelImageCache: {},
      // Add slope icon image
      slopeIcon: null,
      // Add baseline notification
      showBaselineNotification: false,
      showClearModal: false,
      showDeleteRoofModal: false,
      roofToDelete: null,
      // Panel preview cursor properties
      showPanelPreview: false,
      panelPreviewPosition: { x: 0, y: 0 },
      panelPreviewProperties: null, // Will store calculated panel properties
      forcePreviewUpdate: false, // Force preview recalculation
      orientationChanging: false, // Prevent mouse move handler from interfering during orientation change
      lastPreviewUpdate: 0, // Timestamp of last preview update for throttling
      cachedPanelModel: null, // Cache the selected panel model for performance
      cachedPanelDimensions: null, // Cache panel dimensions (width, height in pixels)
      cachedPolygonData: new Map(), // Cache polygon baseline angles and slope data
      lastPolygonIndex: -1, // Track last polygon for caching
      
      // Smart caching for panel dimensions - only recalculates when necessary
      smartPanelCache: {
        panelModelId: null,        // Changes when panel model changes
        orientation: null,          // Changes when orientation changes  
        zoom: null,                 // Changes when zoom changes
        realWorldScale: null,       // Changes when zoom changes
        dimensions: null,           // Cached width/height in pixels
        baseAngle: null,            // Cached baseline angle
        slopeAngle: null,           // Cached slope angle
        slopeFactor: null,          // Cached slope correction factor
        isValid: false              // Whether the cache is valid
      },
      pendingRedraw: false,
      redrawAnimationFrame: null,
      isInitialMount: true, // Flag to track initial mount phase
    };
  },
  computed: {
    canUndo() {
      return this.deleteHistory.length > 0;
    },
    canvasSizeInMeters() {
      return calculateCanvasSizeInMeters();
    },

    totalRoofAreaInMeters() {
      return calculateTotalRoofAreaInMeters(
        this.polygons,
        this.obstacles,
        this.realWorldScale
      );
    },
  },
  mounted() {
    // Initialize canvas with basic setup
    this.initCanvas();

    // Load slope icon
    const slopeImg = new Image();
    slopeImg.src = require("@/assets/slope_icon.png");
    slopeImg.onload = () => {
      this.slopeIcon = slopeImg;
    };

    // Load panel image
    const img = new Image();
    img.src = require("@/assets/panel.png");
    img.onload = () => {
      console.log("Panel image loaded successfully!");
      this.panelImage = img;
      this.redrawCanvas();
    };

    img.onerror = (error) => {
      console.error("Error loading panel image:", error);
    };

    // Load background image and corner coordinates from map state store
    const mapStateStore = useMapStateStore();
    if (mapStateStore.screenshot_info?.imageUrl) {
      const img = new Image();
      img.src = mapStateStore.screenshot_info.imageUrl;
      img.onload = () => {
        this.backgroundImage = img;
        this.redrawCanvas();
      };
    }

    // Load existing drawing data from project store if available
    const projectStore = useProjectStore();
    if (projectStore.project?.drawing_data) {
      const {
        polygons,
        obstacles,
        panels,
        viewportTransform,
        canvasRotation,
        zoom,
      } = projectStore.project.drawing_data;


      // Restore the drawing data
      this.polygons = polygons || [];
      this.obstacles = obstacles || [];
      this.panels = panels || [];
      this.viewportTransform = viewportTransform || { x: 0, y: 0, zoom: 1 };
      this.canvasRotation = canvasRotation || 0;
      this.zoom = zoom || 1;

      // Clear selected panels on mount to avoid confusion
      this.selectedPanels = [];
      this.selectedPanelIndex = -1;

      // Redraw the canvas with restored data
      this.redrawCanvas();
    }

    // Set up corner coordinates and calculate grid size
    if (mapStateStore.screenshot_info?.corners) {
      const { avgScale, gridSize, gridRealSize } = calculateRealWorldScale(
        this.canvasWidth,
        this.canvasHeight
      );

      this.gridSize = gridSize;
      this.gridRealSize = gridRealSize;
      this.realWorldScale = avgScale;
    } else {
      // If no screenshot is available, draw a message
      if (this.ctx) {
        this.ctx.fillStyle = "#f8f9fa";
        this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.ctx.fillStyle = "#666";
        this.ctx.font = "20px Arial";
        this.ctx.textAlign = "center";
        this.ctx.textBaseline = "middle";
        this.ctx.fillText(
          this.$t("project.drawing.noMap"),
          this.canvasWidth / 2,
          this.canvasHeight / 2
        );
      }
    }

    // Handle window resize
    window.addEventListener("resize", this.handleResize);

    // Handle mouse leaving the canvas
    document.addEventListener("mouseup", this.handleGlobalMouseUp);

    // Handle keyboard events
    document.addEventListener("keydown", this.handleKeyDown);
    document.addEventListener("keyup", this.handleKeyUp);
    
    // Mark initial mount as complete after a short delay to ensure all watchers have run
    this.$nextTick(() => {
      setTimeout(() => {
        this.isInitialMount = false;
      }, 100);
    });
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
    document.removeEventListener("mouseup", this.handleGlobalMouseUp);
    document.removeEventListener("keydown", this.handleKeyDown);
    document.removeEventListener("keyup", this.handleKeyUp);
    
    // Clean up any pending animation frames
    if (this.redrawAnimationFrame) {
      cancelAnimationFrame(this.redrawAnimationFrame);
      this.redrawAnimationFrame = null;
    }
  },
  watch: {
    // Add watchers for drawing data changes
    polygons: {
      handler(newPolygons, oldPolygons) {
        this.emitDrawingData();
        // Clear polygon cache when polygons change
        this.cachedPolygonData.clear();
        this.lastPolygonIndex = -1;
      },
      deep: true,
    },
    obstacles: {
      handler(newObstacles) {
        this.emitDrawingData();
      },
      deep: true,
    },
    panels: {
      handler(newPanels) {
        this.emitDrawingData();
      },
      deep: true,
    },
    selectedPanelModel: {
      handler(newModel, oldModel) {
        // Clear cached panel model and dimensions when selection changes
        this.cachedPanelModel = null;
        this.cachedPanelDimensions = null;
        // Invalidate smart cache when panel model changes
        this.smartPanelCache.isValid = false;
        
        // If panel model actually changed (not just initialized), clear all panels
        // But only if we have existing panels, the model actually changed, and we're not in initial mount
        if (oldModel !== undefined && newModel !== oldModel && this.panels.length > 0 && !this.isInitialMount) {
          this.clearAllPanels();
        }
      },
    },
    isVerticalPanel: {
      handler(newOrientation) {
        // Clear cached panel dimensions when orientation changes
        this.cachedPanelDimensions = null;
        // Invalidate smart cache when orientation changes
        this.smartPanelCache.isValid = false;
      },
    },
    // Add watcher for zoom changes to invalidate panel dimension cache
    zoom: {
      handler(newZoom) {
        // Invalidate smart cache when zoom changes (affects realWorldScale calculations)
        this.smartPanelCache.isValid = false;
      },
    },
  },
  methods: {
    // Add new method to emit drawing data
    emitDrawingData() {
      this.$emit("update:drawingData", {
        polygons: this.polygons,
        obstacles: this.obstacles,
        panels: this.panels,
        viewportTransform: this.viewportTransform,
        canvasRotation: this.canvasRotation,
        zoom: this.zoom,
      });
    },

    // Preload panel image based on panel model
    async loadPanelImage(panelModel) {
      if (!panelModel) return;
      
      const imageUrl = getPanelImageUrl(panelModel);
      
      // Check if already cached
      if (this.panelImageCache[imageUrl]) {
        return this.panelImageCache[imageUrl];
      }

      // Load the image
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          this.panelImageCache[imageUrl] = img;
          resolve(img);
          this.scheduleRedraw(); // Redraw to show the new image
        };
        img.onerror = (error) => {
          console.warn(`Failed to load panel image: ${imageUrl}`, error);
          // Cache the fallback image for this URL to avoid repeated attempts
          this.panelImageCache[imageUrl] = this.panelImage;
          resolve(this.panelImage);
        };
        img.src = imageUrl;
      });
    },
    initCanvas() {
      const canvas = document.getElementById("drawingCanvas");
      if (canvas) {
        this.canvasWidth = canvas.parentElement.clientWidth;
        this.canvasHeight = canvas.parentElement.clientHeight;
        canvas.width = this.canvasWidth;
        canvas.height = this.canvasHeight;

        // Store canvas dimensions in the project store if available
        const projectStore = useProjectStore();
        if (projectStore.project?.drawing_data) {
          projectStore.project.drawing_data.canvas_width_px = this.canvasWidth;
          projectStore.project.drawing_data.canvas_height_px =
            this.canvasHeight;
        }

        console.log(
          "canvasWidth in store",
          projectStore.project.drawing_data.canvas_width_px
        );
        console.log(
          "canvasHeight in store",
          projectStore.project.drawing_data.canvas_height_px
        );

        // Get context for drawing
        this.ctx = canvas.getContext("2d");
        this.ctx.fillStyle = "#f8f9fa";
        this.ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    },

    handleResize() {
      const canvas = document.getElementById("drawingCanvas");
      if (canvas) {
        this.canvasWidth = canvas.parentElement.clientWidth;
        this.canvasHeight = canvas.parentElement.clientHeight;
        canvas.width = this.canvasWidth;
        canvas.height = this.canvasHeight;

        // Invalidate smart cache on resize (viewport changes)
        this.smartPanelCache.isValid = false;

        // Redraw everything on resize
        this.scheduleRedraw();
      }
    },

    setTool(tool) {
      // Check if trying to use panel tool
      if (tool === "addPanel") {
        // Check if a panel model is selected
        if (!this.selectedPanelModel) {
          this.showPanelNotification = true;
          setTimeout(() => {
            this.showPanelNotification = false;
          }, 3000);
          return;
        }
        
        // Invalidate smart cache when switching to panel tool (ensures fresh calculations)
        this.smartPanelCache.isValid = false;
      }

      this.currentTool = tool;

      // Reset all tool-specific states when changing tools
      if (this.currentTool !== "roof") {
        this.isDrawing = false;
        this.tempPoint = null;
        this.closePath = false;
      }

      if (this.currentTool !== "slope") {
        this.showSlopePopup = false;
        this.selectingBaseLine = false;
        this.selectedPolygonForSlope = -1;
        this.selectedLineForSlope = -1;
      }

      if (this.currentTool !== "move") {
        // Reset pan/zoom when switching away from move tool
        //this.resetView();
      }

      // Reset panel preview when switching away from panel tool
      if (this.currentTool !== "addPanel") {
        this.showPanelPreview = false;
        this.panelPreviewProperties = null;
      }

      this.redrawCanvas();
    },

    // Zoom and pan related methods
    resetView() {
      this.viewportTransform = { x: 0, y: 0, zoom: 1 };
      this.zoom = 1;
      this.canvasRotation = 0;
      this.redrawCanvas();
    },

    zoomIn() {
      if (this.zoom < this.maxZoom) {
        this.zoom = Math.min(this.maxZoom, this.zoom + this.zoomStep);
        this.applyZoom();
      }
    },

    zoomOut() {
      if (this.zoom > this.minZoom) {
        this.zoom = Math.max(this.minZoom, this.zoom - this.zoomStep);
        this.applyZoom();
      }
    },

    applyZoom() {
      this.scheduleRedraw();
    },

    handleCanvasWheel(event) {
      if (this.currentTool !== "move") return;

      event.preventDefault();

      // Get mouse position in canvas
      const rect = event.target.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;

      // Determine zoom direction
      if (event.deltaY < 0) {
        // Zoom in
        if (this.zoom < this.maxZoom) {
          this.zoom = Math.min(this.maxZoom, this.zoom + this.zoomStep);
        }
      } else {
        // Zoom out
        if (this.zoom > this.minZoom) {
          this.zoom = Math.max(this.minZoom, this.zoom - this.zoomStep);
        }
      }

      // Invalidate smart cache when zoom changes (affects panel dimensions)
      this.smartPanelCache.isValid = false;

      this.applyZoom();
    },

    // Convert screen coordinates to canvas coordinates considering zoom and pan
    screenToCanvas(x, y) {
      // Account for translation to center, rotation, and scaling
      const centerX = this.canvasWidth / 2;
      const centerY = this.canvasHeight / 2;

      // First, adjust for pan
      const adjustedX = x - centerX - this.viewportTransform.x;
      const adjustedY = y - centerY - this.viewportTransform.y;

      // Apply inverse rotation
      const cosTheta = Math.cos(-this.canvasRotation);
      const sinTheta = Math.sin(-this.canvasRotation);
      const rotatedX = adjustedX * cosTheta - adjustedY * sinTheta;
      const rotatedY = adjustedX * sinTheta + adjustedY * cosTheta;

      // Adjust back to center and apply zoom
      return {
        x: rotatedX / this.zoom + centerX,
        y: rotatedY / this.zoom + centerY,
      };
    },

    // Convert canvas coordinates to screen coordinates
    canvasToScreen(x, y) {
      const centerX = this.canvasWidth / 2;
      const centerY = this.canvasHeight / 2;

      // First apply zoom and adjust for center
      const zoomedX = (x - centerX) * this.zoom;
      const zoomedY = (y - centerY) * this.zoom;

      // Apply rotation
      const cosTheta = Math.cos(this.canvasRotation);
      const sinTheta = Math.sin(this.canvasRotation);
      const rotatedX = zoomedX * cosTheta - zoomedY * sinTheta;
      const rotatedY = zoomedX * sinTheta + zoomedY * cosTheta;

      // Finally adjust for pan
      return {
        x: rotatedX + centerX + this.viewportTransform.x,
        y: rotatedY + centerY + this.viewportTransform.y,
      };
    },

    handleGlobalMouseUp(event) {
      // Handle case when mouse is released outside the canvas
      this.handleCanvasMouseUp(event);
    },

    handleCanvasMouseDown(event) {
      if (!this.currentTool) return;

      // Check if Ctrl key is pressed for multi-select
      const isCtrlPressed = event.ctrlKey || event.metaKey; // metaKey for Mac
      this.isMultiSelectMode = isCtrlPressed;

      const rect = event.target.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      // Get canvas coordinates with proper rotation handling
      const canvasPoint = this.screenToCanvas(x, y);

      // If in move tool mode, handle panel selection and dragging
      if (this.currentTool === "move") {
        // Find if we clicked on a panel
        const panelIndex = this.findTopMostPanelAt(
          canvasPoint.x,
          canvasPoint.y
        );

        if (panelIndex >= 0) {
          if (isCtrlPressed) {
            // If panel is already selected, deselect it
            const existingIndex = this.selectedPanels.indexOf(panelIndex);
            if (existingIndex !== -1) {
              this.selectedPanels.splice(existingIndex, 1);
            } else {
              // Otherwise add to selection
              this.selectedPanels.push(panelIndex);
            }
          } else {
            // If not using multi-select, clear previous selection
            if (!this.selectedPanels.includes(panelIndex)) {
              this.selectedPanels = [panelIndex];
            }
            this.selectedPanelIndex = panelIndex;
            this.isDraggingPanel = true;
          }

          this.redrawCanvas();
          if (!isCtrlPressed) {
            return;
          }
        } else if (!isCtrlPressed) {
          // If clicking empty space without Ctrl, clear selection
          this.selectedPanels = [];
          this.redrawCanvas();
        }

        // If no panel found or we're in multi-select mode, handle panning
        if (!this.isDraggingPanel) {
          this.isPanning = true;
          this.lastPanPoint = { x, y };
        }
        return;
      }

      // If in delete tool mode, delete the selected polygon, obstacle, or panel
      if (this.currentTool === "delete") {
        // First try to delete a panel
        const panelIndex = this.findTopMostPanelAt(
          canvasPoint.x,
          canvasPoint.y
        );
        if (panelIndex >= 0) {
          // Store the panel in history before deleting
          this.addToDeleteHistory("panel", panelIndex, this.panels[panelIndex]);
          this.panels.splice(panelIndex, 1);
          this.redrawCanvas();
          return;
        }

        // Then try to delete an obstacle
        const obstacleIndex = this.findTopMostObstacleAt(
          canvasPoint.x,
          canvasPoint.y
        );
        if (obstacleIndex >= 0) {
          // Store the obstacle in history before deleting
          this.addToDeleteHistory(
            "obstacle",
            obstacleIndex,
            this.obstacles[obstacleIndex]
          );
          this.obstacles.splice(obstacleIndex, 1);
          this.redrawCanvas();
          return;
        }

        // Finally try to delete a roof
        const polygonIndex = this.findTopMostPolygonAt(
          canvasPoint.x,
          canvasPoint.y
        );
        if (polygonIndex >= 0) {
          // Show confirmation modal for roof deletion
          this.roofToDelete = polygonIndex;
          this.showDeleteRoofModal = true;
          return;
        }
      }

      // Handle panel placement
      if (this.currentTool === "addPanel") {
        // Find if we clicked on a polygon
        const polygonIndex = this.findTopMostPolygonAt(
          canvasPoint.x,
          canvasPoint.y
        );

        if (polygonIndex >= 0) {
          // Get the selected panel model
          const equipmentStore = useEquipmentStore();
          const selectedPanel = equipmentStore.panels.find(
            (p) => p.model_number === this.selectedPanelModel
          );

          if (selectedPanel) {
            // Create a new panel at the clicked position
            this.createPanel(canvasPoint, polygonIndex, selectedPanel);
          }
        }
        return;
      }

      // If in selecting base line mode
      if (this.selectingBaseLine) {
        // Find the nearest line to the click point
        const lineInfo = this.findNearestLineInPolygon(
          canvasPoint.x,
          canvasPoint.y,
          this.selectedPolygonForSlope
        );

        if (lineInfo) {
          this.selectedLineForSlope = lineInfo;
          this.selectingBaseLine = false;
          // Show the popup again with the Apply button
          this.showSlopePopup = true;
          this.redrawCanvas();
        }
        return;
      }

      // If in slope tool mode and not selecting base line
      if (this.currentTool === "slope") {
        // Find if we clicked on a polygon
        const polygonIndex = this.findTopMostPolygonAt(
          canvasPoint.x,
          canvasPoint.y
        );

        if (polygonIndex >= 0) {
          this.selectedPolygonForSlope = polygonIndex;
          this.showSlopePopup = true;
        }
        return;
      }

      // If in roof tool mode
      if (this.currentTool === "roof") {
        // Check if we're hovering over an existing point (for editing)
        if (this.hoverPointInfo && !this.isDrawing) {
          this.selectedPolygonIndex = this.hoverPointInfo.polygonIndex;
          this.selectedPointIndex = this.hoverPointInfo.pointIndex;
          this.isDragging = true;
          return;
        }

        // Check if we're near the starting point to close the polygon
        if (
          this.currentPolygon.length > 2 &&
          this.isNearStartPoint(canvasPoint.x, canvasPoint.y)
        ) {
          this.completePolygon();
          return;
        }

        // Start drawing or continue adding points
        if (!this.isDrawing) {
          this.isDrawing = true;
          this.currentPolygon = [{ x: canvasPoint.x, y: canvasPoint.y }];
          // Immediate redraw to show the starting point
          this.redrawCanvas();
        } else {
          this.currentPolygon.push({ x: canvasPoint.x, y: canvasPoint.y });
          // Immediate redraw to show the new point
          this.redrawCanvas();
        }
      }

      // If in obstacle tool mode
      if (this.currentTool === "obstacle") {
        // Only allow drawing obstacles inside an existing polygon
        const polygonIndex = this.findTopMostPolygonAt(
          canvasPoint.x,
          canvasPoint.y
        );

        if (!this.isDrawing) {
          // Can only start drawing if inside a polygon
          if (polygonIndex >= 0) {
            this.parentShapeIndex = polygonIndex;
            this.isDrawing = true;
            this.currentPolygon = [{ x: canvasPoint.x, y: canvasPoint.y }];
            // Immediate redraw to show the starting point
            this.redrawCanvas();
          }
        } else {
          // Check if we're near the starting point to close the obstacle
          if (
            this.currentPolygon.length > 2 &&
            this.isNearStartPoint(canvasPoint.x, canvasPoint.y)
          ) {
            this.completeObstacle();
            return;
          }

          // Only continue drawing if still inside the parent polygon
          if (
            this.isPointInPolygon(
              canvasPoint.x,
              canvasPoint.y,
              this.polygons[this.parentShapeIndex]
            )
          ) {
            this.currentPolygon.push({ x: canvasPoint.x, y: canvasPoint.y });
            // Immediate redraw to show the new point
            this.redrawCanvas();
          }
        }
      }

      // If in rotate tool mode
      if (this.currentTool === "rotate") {
        // Find if we clicked on a polygon
        const polygonIndex = this.findTopMostPolygonAt(
          canvasPoint.x,
          canvasPoint.y
        );

        if (polygonIndex >= 0) {
          this.rotateCanvasToPolygon(polygonIndex);
        } else {
          // If clicked outside any polygon, reset to original orientation
          this.resetCanvasOrientation();
        }
        return;
      }

      this.redrawCanvas();
    },

    handleCanvasMouseMove(event) {
      const rect = event.target.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;

      // Get canvas coordinates with proper rotation handling
      const canvasPoint = this.screenToCanvas(x, y);

      // Track if preview needs updating for conditional canvas redraw
      let shouldUpdate = false;

      // Handle panel dragging if in move mode
      if (
        this.isDraggingPanel &&
        this.currentTool === "move" &&
        this.selectedPanelIndex >= 0
      ) {
        const panel = this.panels[this.selectedPanelIndex];

        // Calculate the offset from the panel's center
        const centerX =
          panel.points.reduce((sum, p) => sum + p.x, 0) / panel.points.length;
        const centerY =
          panel.points.reduce((sum, p) => sum + p.y, 0) / panel.points.length;

        const offsetX = canvasPoint.x - centerX;
        const offsetY = canvasPoint.y - centerY;

        // Create potential new position for the selected panel
        const newPoints = panel.points.map((point) => ({
          x: point.x + offsetX,
          y: point.y + offsetY,
        }));

        // Calculate the final offset to apply to all selected panels
        const finalOffsetX = offsetX;
        const finalOffsetY = offsetY;

        // Move all selected panels (including the dragged one)
        for (const selectedIndex of this.selectedPanels) {
          const selectedPanel = this.panels[selectedIndex];
          selectedPanel.points = selectedPanel.points.map((point) => ({
            x: point.x + finalOffsetX,
            y: point.y + finalOffsetY,
          }));
        }

        this.redrawCanvas();
        return;
      }

      // Handle panning if in move mode
      if (this.isPanning && this.currentTool === "move") {
        const dx = x - this.lastPanPoint.x;
        const dy = y - this.lastPanPoint.y;

        this.viewportTransform.x += dx;
        this.viewportTransform.y += dy;
        this.lastPanPoint = { x, y };

        this.redrawCanvas();
        return;
      }

      // If dragging a point, update its position
      if (
        this.isDragging &&
        this.selectedPolygonIndex >= 0 &&
        this.selectedPointIndex >= 0
      ) {
        this.polygons[this.selectedPolygonIndex][this.selectedPointIndex] = {
          x: canvasPoint.x,
          y: canvasPoint.y,
        };
        this.redrawCanvas();
        return;
      }

      // Check for hover over points - need to convert all points to screen coordinates
      this.hoverPointInfo = this.findPointAt(canvasPoint.x, canvasPoint.y);

      // If in roof drawing mode or obstacle drawing mode
      if (
        (this.currentTool === "roof" || this.currentTool === "obstacle") &&
        this.isDrawing
      ) {
        // For obstacle tool, only update the temp point if inside the parent polygon
        if (this.currentTool === "obstacle") {
          if (
            this.parentShapeIndex >= 0 &&
            this.isPointInPolygon(
              canvasPoint.x,
              canvasPoint.y,
              this.polygons[this.parentShapeIndex]
            )
          ) {
            this.tempPoint = { x: canvasPoint.x, y: canvasPoint.y };
          }
        } else {
          // For roof tool, always update the temp point
          this.tempPoint = { x: canvasPoint.x, y: canvasPoint.y };
        }

        // Check if we're near the starting point to show the snap indicator
        if (this.currentPolygon.length > 2) {
          this.closePath = this.isNearStartPoint(canvasPoint.x, canvasPoint.y);
      }

        // For polygon drawing, we need immediate redraw to show the temp line
        if (this.isDrawing && (this.currentTool === "roof" || this.currentTool === "obstacle")) {
      this.redrawCanvas();
        }
      }

      // Handle panel preview cursor when in addPanel tool mode
      if (this.currentTool === "addPanel" && this.selectedPanelModel && !this.orientationChanging) {
        const now = Date.now();
        const timeSinceLastUpdate = now - this.lastPreviewUpdate;

        // Maintain accuracy: do ray casting on every mouse move like the simple cursor
        // But throttle the full panel recalculation for performance
        const shouldRecalculatePanel = this.forcePreviewUpdate ||
          (timeSinceLastUpdate > 33 && (
            !this.panelPreviewPosition ||
            Math.abs(this.panelPreviewPosition.x - canvasPoint.x) > 2 ||
            Math.abs(this.panelPreviewPosition.y - canvasPoint.y) > 2
          ));

        // Always find polygon for accuracy (like simple cursor), but throttle panel calculations
        shouldUpdate = shouldRecalculatePanel;

        // Always find polygon for accuracy (like simple cursor)
        const polygonIndex = this.findTopMostPolygonAt(
          canvasPoint.x,
          canvasPoint.y
        );

        // Only recalculate panel properties if needed (throttled for performance)
        if (shouldUpdate || !this.panelPreviewProperties || polygonIndex !== this.lastPolygonIndex) {
          this.lastPolygonIndex = polygonIndex;

          if (polygonIndex >= 0) {
            // Calculate panel preview properties
            this.panelPreviewProperties = this.calculatePanelPreview(
              canvasPoint,
              polygonIndex
            );
            this.lastPreviewUpdate = now;
            this.forcePreviewUpdate = false; // Reset the force flag
          } else {
            // Hide preview if not over a polygon
            this.showPanelPreview = false;
            this.panelPreviewProperties = null;
            this.forcePreviewUpdate = false; // Reset the force flag
          }
        } else if (this.panelPreviewProperties && this.lastPolygonIndex >= 0) {
          // Fast path: just update the panel position without recalculating everything
          const { dimensions, baseAngle, slopeAngle } = this.smartPanelCache;
          if (dimensions) {
            const halfWidth = dimensions.width / 2;
            const halfHeight = dimensions.height / 2;

            this.panelPreviewProperties.points = [
              { x: canvasPoint.x - halfWidth, y: canvasPoint.y - halfHeight },
              { x: canvasPoint.x + halfWidth, y: canvasPoint.y - halfHeight },
              { x: canvasPoint.x + halfWidth, y: canvasPoint.y + halfHeight },
              { x: canvasPoint.x - halfWidth, y: canvasPoint.y + halfHeight },
            ];
          }
        }

        // Always update position for reference
        this.panelPreviewPosition = canvasPoint;
        if (this.panelPreviewProperties) {
          this.showPanelPreview = true;
        }

        // Schedule redraw for smooth animation
        this.scheduleRedraw();
      } else if (this.currentTool !== "addPanel") {
        // Hide preview if not in panel tool mode
        this.showPanelPreview = false;
        this.panelPreviewProperties = null;
        this.lastPolygonIndex = -1; // Reset cached polygon
      }
    },

    handleCanvasMouseUp(event) {
      // Reset panel dragging state
      this.isDraggingPanel = false;
      this.selectedPanelIndex = -1;

      // Keep selected panels selected after dragging

      // Reset panning state
      this.isPanning = false;
      this.lastPanPoint = null;

      // Reset dragging state
      this.isDragging = false;
      this.selectedPolygonIndex = -1;
      this.selectedPointIndex = -1;
    },

    isNearStartPoint(x, y) {
      if (this.currentPolygon.length === 0) return false;

      const startPoint = this.currentPolygon[0];
      const distance = Math.sqrt(
        Math.pow(startPoint.x - x, 2) + Math.pow(startPoint.y - y, 2)
      );

      return distance <= this.snapDistance / this.zoom; // Adjust snap distance based on zoom
    },

    findPointAt(x, y) {
      const selectionRadius = 8 / this.zoom; // Adjust selection radius based on zoom

      // Check existing polygons
      for (let i = 0; i < this.polygons.length; i++) {
        const polygon = this.polygons[i];
        for (let j = 0; j < polygon.length; j++) {
          const point = polygon[j];
          const distance = Math.sqrt(
            Math.pow(point.x - x, 2) + Math.pow(point.y - y, 2)
          );
          if (distance <= selectionRadius) {
            return { polygonIndex: i, pointIndex: j };
          }
        }
      }
      return null;
    },

    completePolygon() {
      // Close the polygon by connecting back to the first point
      if (this.currentPolygon.length > 2) {
        // Store the completed polygon
        this.polygons.push([...this.currentPolygon]);

        // Reset for next polygon
        this.currentPolygon = [];
        this.isDrawing = false;
        this.tempPoint = null;
        this.closePath = false;

        this.scheduleRedraw();
      }
    },

    completeObstacle() {
      // Close the obstacle by connecting back to the first point
      if (this.currentPolygon.length > 2 && this.parentShapeIndex >= 0) {
        // Store the completed obstacle with reference to parent
        this.obstacles.push({
          points: [...this.currentPolygon],
          parentIndex: this.parentShapeIndex,
        });

        // Reset for next obstacle
        this.currentPolygon = [];
        this.isDrawing = false;
        this.tempPoint = null;
        this.closePath = false;
        this.parentShapeIndex = -1;

        this.scheduleRedraw();
      }
    },

    redrawCanvas() {
      if (!this.ctx) return;

      // Clear the canvas
      this.ctx.fillStyle = "#f8f9fa";
      this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

      // Apply transformations for pan, zoom and rotation
      this.ctx.save();

      // Disable image smoothing to speed up scaled redraws during zooming
      this.ctx.imageSmoothingEnabled = false;

      // Center of canvas for rotation
      const centerX = this.canvasWidth / 2;
      const centerY = this.canvasHeight / 2;

      // Apply transformations in the correct order
      this.ctx.translate(
        centerX + this.viewportTransform.x,
        centerY + this.viewportTransform.y
      );
      this.ctx.rotate(this.canvasRotation);
      this.ctx.scale(this.zoom, this.zoom);
      this.ctx.translate(-centerX, -centerY);

      // Draw background image if loaded
      if (this.backgroundImage) {
        // Calculate the dimensions to cover the entire canvas
        const scale = Math.max(
          this.canvasWidth / this.backgroundImage.width,
          this.canvasHeight / this.backgroundImage.height
        );
        const width = this.backgroundImage.width * scale;
        const height = this.backgroundImage.height * scale;

        // Center the image
        const x = (this.canvasWidth - width) / 2;
        const y = (this.canvasHeight - height) / 2;

        this.ctx.drawImage(this.backgroundImage, x, y, width, height);
      }

      // Draw a grid to show the pan/zoom effect more clearly
      this.drawGrid();

      // Draw saved polygons (roofs)
      this.polygons.forEach((poly, polyIndex) => {
        this.drawPolygon(poly, "#4CAF50", true, polyIndex);

        // Draw slope indicator if available
        if (poly.metadata && poly.metadata.slope) {
          this.drawSlopeIndicator(poly, poly.metadata.slope, polyIndex);
        }
      });

      // Draw saved obstacles
      this.obstacles.forEach((obstacle, obstacleIndex) => {
        this.drawObstacle(obstacle.points, obstacleIndex);
      });

      // Draw panels
      this.panels.forEach((panel, index) => {
        this.drawPanel(panel, index);
      });

      // Draw panel preview cursor if in panel tool mode
      if (this.showPanelPreview && this.panelPreviewProperties) {
        this.drawPanelPreview(this.panelPreviewProperties);
      }

      

      // Draw current polygon being drawn
      if (this.currentPolygon.length > 0) {
        // Determine color based on tool
        const color = this.currentTool === "obstacle" ? "#FF5252" : "#4CAF50";

        // Draw the polygon outline
        this.drawPolygon(this.currentPolygon, color, false);

        // Draw the temporary line from last point to cursor
        if (this.tempPoint && this.isDrawing) {
          const lastPoint = this.currentPolygon[this.currentPolygon.length - 1];

          this.ctx.beginPath();
          this.ctx.moveTo(lastPoint.x, lastPoint.y);
          this.ctx.lineTo(this.tempPoint.x, this.tempPoint.y);
          this.ctx.strokeStyle = color;
          this.ctx.lineWidth = 2 / this.zoom; // Adjust line width based on zoom
          this.ctx.stroke();

          // If near the start point, show an indicator to close
          if (this.closePath) {
            const startPoint = this.currentPolygon[0];

            // Draw an indicator that path will close
            this.ctx.beginPath();
            this.ctx.moveTo(this.tempPoint.x, this.tempPoint.y);
            this.ctx.lineTo(startPoint.x, startPoint.y);
            this.ctx.strokeStyle = "#FF9800"; // Orange to indicate closing
            this.ctx.setLineDash([5 / this.zoom, 3 / this.zoom]); // Scale dash pattern with zoom
            this.ctx.stroke();
            this.ctx.setLineDash([]); // Reset to solid line

            // Draw a circle around the start point
            this.ctx.beginPath();
            this.ctx.arc(
              startPoint.x,
              startPoint.y,
              8 / this.zoom,
              0,
              Math.PI * 2
            );
            this.ctx.fillStyle = "rgba(255, 152, 0, 0.3)";
            this.ctx.fill();
            this.ctx.strokeStyle = "#FF9800";
            this.ctx.stroke();
          }
        }

        // Draw points of current polygon
        this.currentPolygon.forEach((point, index) => {
          this.drawPoint(
            point,
            index === 0,
            false,
            this.currentTool === "obstacle"
          );
        });
      }

      // Reset transformations
      this.ctx.restore();
    },

    drawGrid() {
      const gridSize = this.gridSize;
      const gridColor = "rgba(250, 250, 250, 0.4)"; // Consistent color for all lines

      // Calculate grid boundaries based on canvas size and current transform
      const topLeft = this.screenToCanvas(0, 0);
      const bottomRight = this.screenToCanvas(
        this.canvasWidth,
        this.canvasHeight
      );

      // Round to nearest grid line
      const startX = Math.floor(topLeft.x / gridSize) * gridSize;
      const startY = Math.floor(topLeft.y / gridSize) * gridSize;
      const endX = Math.ceil(bottomRight.x / gridSize) * gridSize;
      const endY = Math.ceil(bottomRight.y / gridSize) * gridSize;

      this.ctx.strokeStyle = gridColor;
      this.ctx.lineWidth = 1.5 / this.zoom; // Consistent line width for all lines

      // Draw vertical grid lines
      for (let x = startX; x <= endX; x += gridSize) {
        this.ctx.beginPath();
        this.ctx.moveTo(x, startY);
        this.ctx.lineTo(x, endY);
        this.ctx.stroke();
      }

      // Draw horizontal grid lines
      for (let y = startY; y <= endY; y += gridSize) {
        this.ctx.beginPath();
        this.ctx.moveTo(startX, y);
        this.ctx.lineTo(endX, y);
        this.ctx.stroke();
      }
    },

    drawPolygon(points, color, isClosed, polygonIndex = -1) {
      if (!this.ctx || points.length === 0) return;

      this.ctx.beginPath();
      this.ctx.moveTo(points[0].x, points[0].y);

      for (let i = 1; i < points.length; i++) {
        this.ctx.lineTo(points[i].x, points[i].y);
      }

      if (isClosed) {
        this.ctx.closePath();
        this.ctx.fillStyle = "rgba(76, 175, 80, 0.2)"; // Light green with transparency
        this.ctx.fill();
      }

      this.ctx.strokeStyle = color;
      this.ctx.lineWidth = 2 / this.zoom; // Adjust line width based on zoom
      this.ctx.stroke();

      // Draw line lengths
      for (let i = 0; i < points.length; i++) {
        const j = (i + 1) % points.length;
        const p1 = points[i];
        const p2 = points[j];
        // Calculate line length in meters
        const length = calculateLineLength(p1, p2, this.realWorldScale);

        // Calculate midpoint of the line
        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;

        // Calculate angle of the line
        const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);

        // Draw length text
        this.ctx.save();
        this.ctx.translate(midX, midY);
        this.ctx.rotate(angle);
        this.ctx.textAlign = "center";
        this.ctx.textBaseline = "middle";
        this.ctx.font = `${12 / this.zoom}px Arial`;
        this.ctx.fillStyle = "#666";

        // Offset the text perpendicular to the line
        this.ctx.translate(0, -15 / this.zoom);
        this.ctx.fillText(`${length.toFixed(1)}m`, 0, 0);
        this.ctx.restore();
      }

      // Draw control points for completed polygons
      if (polygonIndex >= 0) {
        points.forEach((point, pointIndex) => {
          const isHovered =
            this.hoverPointInfo &&
            this.hoverPointInfo.polygonIndex === polygonIndex &&
            this.hoverPointInfo.pointIndex === pointIndex;

          this.drawPoint(point, pointIndex === 0, isHovered);
        });

        // Highlight selected base line for slope if applicable
        if (
          polygonIndex === this.selectedPolygonForSlope &&
          this.selectedLineForSlope !== -1
        ) {
          const { startIndex, endIndex } = this.selectedLineForSlope;
          const startPoint = points[startIndex];
          const endPoint = points[endIndex];

          this.ctx.beginPath();
          this.ctx.moveTo(startPoint.x, startPoint.y);
          this.ctx.lineTo(endPoint.x, endPoint.y);
          this.ctx.strokeStyle = "#2196F3"; // Blue for selected base line
          this.ctx.lineWidth = 3 / this.zoom; // Make it slightly thicker
          this.ctx.stroke();
        }

        // If in slope select mode, highlight the polygon being worked on
        if (
          polygonIndex === this.selectedPolygonForSlope &&
          this.selectingBaseLine
        ) {
          this.ctx.strokeStyle = "#FFC107"; // Amber for highlighting during selection
          this.ctx.lineWidth = 2 / this.zoom;
          this.ctx.setLineDash([5 / this.zoom, 3 / this.zoom]);
          this.ctx.stroke();
          this.ctx.setLineDash([]); // Reset to solid line
        }
      }
    },

    drawPoint(
      point,
      isStartPoint = false,
      isHovered = false,
      isObstacle = false
    ) {
      const radius = (isHovered ? 6 : 4) / this.zoom; // Adjust radius based on zoom

      this.ctx.beginPath();
      this.ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);

      if (isHovered) {
        this.ctx.fillStyle = "#FF9800"; // Orange for hovered points
      } else if (isStartPoint && !isObstacle) {
        this.ctx.fillStyle = "#2196F3"; // Blue for roof start point
      } else if (isStartPoint && isObstacle) {
        this.ctx.fillStyle = "#9C27B0"; // Purple for obstacle start point
      } else if (isObstacle) {
        this.ctx.fillStyle = "#FF5252"; // Red for obstacle points
      } else {
        this.ctx.fillStyle = "#4CAF50"; // Green for roof points
      }

      this.ctx.fill();
      this.ctx.strokeStyle = "#FFFFFF";
      this.ctx.lineWidth = 1 / this.zoom; // Adjust line width based on zoom
      this.ctx.stroke();
    },

    // Draw an obstacle
    drawObstacle(points, obstacleIndex) {
      if (!this.ctx || points.length === 0) return;

      this.ctx.beginPath();
      this.ctx.moveTo(points[0].x, points[0].y);

      for (let i = 1; i < points.length; i++) {
        this.ctx.lineTo(points[i].x, points[i].y);
      }

      this.ctx.closePath();
      this.ctx.fillStyle = "rgba(255, 82, 82, 0.4)"; // Red with transparency
      this.ctx.fill();

      this.ctx.strokeStyle = "#FF5252"; // Red outline
      this.ctx.lineWidth = 2 / this.zoom; // Adjust line width based on zoom
      this.ctx.stroke();

      // Draw control points
      points.forEach((point, pointIndex) => {
        const isHovered =
          this.hoverPointInfo &&
          this.hoverPointInfo.obstacleIndex === obstacleIndex &&
          this.hoverPointInfo.pointIndex === pointIndex;

        this.drawPoint(point, pointIndex === 0, isHovered, true);
      });
    },

    // Find the top-most (newest) polygon that contains the given point
    findTopMostPolygonAt(x, y) {
      // Iterate in reverse (newest to oldest) to find the top-most shape
      for (let i = this.polygons.length - 1; i >= 0; i--) {
        if (this.isPointInPolygon(x, y, this.polygons[i])) {
          return i;
        }
      }
      return -1;
    },

    // Check if a point is inside a polygon using ray casting algorithm
    isPointInPolygon(x, y, polygon) {
      let inside = false;

      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x;
        const yi = polygon[i].y;
        const xj = polygon[j].x;
        const yj = polygon[j].y;

        const intersect =
          yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

        if (intersect) inside = !inside;
      }

      return inside;
    },

    // Find the top-most obstacle that contains the given point
    findTopMostObstacleAt(x, y) {
      // Iterate in reverse (newest to oldest) to find the top-most obstacle
      for (let i = this.obstacles.length - 1; i >= 0; i--) {
        if (this.isPointInPolygon(x, y, this.obstacles[i].points)) {
          return i;
        }
      }
      return -1;
    },

    // Find the nearest line segment in a polygon to a point
    findNearestLineInPolygon(x, y, polygonIndex) {
      if (polygonIndex < 0 || polygonIndex >= this.polygons.length) {
        return null;
      }

      const polygon = this.polygons[polygonIndex];
      let nearestLine = null;
      let minDistance = Infinity;

      // Check each line segment in the polygon
      for (let i = 0; i < polygon.length; i++) {
        const p1 = polygon[i];
        const p2 = polygon[(i + 1) % polygon.length]; // Wrap around to the first point

        // Calculate distance from point to line segment
        const distance = this.distanceToLineSegment(
          x,
          y,
          p1.x,
          p1.y,
          p2.x,
          p2.y
        );

        if (distance < minDistance) {
          minDistance = distance;
          nearestLine = { startIndex: i, endIndex: (i + 1) % polygon.length };
        }
      }

      // Only consider it a match if it's reasonably close (10px when zoomed)
      const thresholdDistance = 10 / this.zoom;
      if (minDistance <= thresholdDistance) {
        return nearestLine;
      }

      return null;
    },

    // Calculate distance from a point to a line segment
    distanceToLineSegment(px, py, x1, y1, x2, y2) {
      const A = px - x1;
      const B = py - y1;
      const C = x2 - x1;
      const D = y2 - y1;

      const dot = A * C + B * D;
      const lenSq = C * C + D * D;
      let param = -1;

      if (lenSq !== 0) {
        param = dot / lenSq;
      }

      let xx, yy;

      if (param < 0) {
        xx = x1;
        yy = y1;
      } else if (param > 1) {
        xx = x2;
        yy = y2;
      } else {
        xx = x1 + param * C;
        yy = y1 + param * D;
      }

      const dx = px - xx;
      const dy = py - yy;

      return Math.sqrt(dx * dx + dy * dy);
    },

    // Slope functionality methods
    startSelectingBaseLine() {
      this.selectingBaseLine = true;
      this.selectedLineForSlope = -1;
      // Hide the popup while selecting the base line
      this.showSlopePopup = false;
      this.redrawCanvas();
    },

    cancelSlope() {
      this.showSlopePopup = false;
      this.selectingBaseLine = false;
      this.selectedPolygonForSlope = -1;
      this.selectedLineForSlope = -1;
      this.redrawCanvas();
    },

    applySlope() {
      // Add slope data to the polygon
      if (this.polygons[this.selectedPolygonForSlope]) {
        if (!this.polygons[this.selectedPolygonForSlope].metadata) {
          this.polygons[this.selectedPolygonForSlope].metadata = {};
        }
        this.polygons[this.selectedPolygonForSlope].metadata.slope = {
          value: this.slopeValue,
          baseLine: this.selectedLineForSlope,
        };
      }

      // Reset the UI state
      this.showSlopePopup = false;
      this.selectingBaseLine = false;
      this.selectedPolygonForSlope = -1;
      this.selectedLineForSlope = -1;
      this.redrawCanvas();
    },

    // Handle keyboard events
    handleKeyDown(event) {
      // If ESC key is pressed
      if (event.key === "Escape") {
        // Clear panel selection
        this.selectedPanels = [];

        // Other ESC key handling
        if (this.currentTool === "slope") {
          this.cancelSlope();
        }

        // Cancel any unfinished drawings
        if (
          (this.currentTool === "roof" || this.currentTool === "obstacle") &&
          this.isDrawing
        ) {
          // Reset drawing state
          this.currentPolygon = [];
          this.isDrawing = false;
          this.tempPoint = null;
          this.closePath = false;
          this.parentShapeIndex = -1;
        }

        // Switch to move mode
        this.setTool("move");

        // Redraw canvas
        this.redrawCanvas();
      }

      // Track Ctrl key for multi-select
      if (event.ctrlKey || event.metaKey) {
        this.isMultiSelectMode = true;
      }

      // Select all panels with Ctrl+A
      if ((event.ctrlKey || event.metaKey) && event.key === "a") {
        event.preventDefault();
        if (this.currentTool === "move") {
          this.selectedPanels = this.panels.map((_, index) => index);
          this.redrawCanvas();
        }
      }
    },

    // Track when Ctrl key is released
    handleKeyUp(event) {
      if (event.key === "Control" || event.key === "Meta") {
        this.isMultiSelectMode = false;
      }
    },

    // Draw a visual indicator for the roof slope
    drawSlopeIndicator(polygon, slopeData, polygonIndex) {
      const { value, baseLine } = slopeData;
      const { startIndex, endIndex } = baseLine;

      // Get the baseline points
      const p1 = polygon[startIndex];
      const p2 = polygon[endIndex];

      // Calculate the midpoint of the baseline
      const midpoint = {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2,
      };

      // Calculate the baseline length
      const baseLength = Math.sqrt(
        Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)
      );

      // Calculate the angle of the baseline
      const baseAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);

      // Calculate the perpendicular angle (90 degrees rotated)
      const perpAngle = baseAngle - Math.PI / 2;

      // Convert slope angle from degrees to radians
      const slopeRadian = (value * Math.PI) / 180;

      // Calculate the height of the slope indicator based on slope angle
      const indicatorHeight = baseLength * Math.tan(slopeRadian) * 0.25;

      // Calculate the endpoint for the slope indicator
      const endpoint = {
        x: midpoint.x + Math.cos(perpAngle) * indicatorHeight,
        y: midpoint.y + Math.sin(perpAngle) * indicatorHeight,
      };

      // Draw the slope indicator line
      this.ctx.beginPath();
      this.ctx.moveTo(midpoint.x, midpoint.y);
      this.ctx.lineTo(endpoint.x, endpoint.y);
      this.ctx.strokeStyle = "#673AB7"; // color for the slope indicator
      this.ctx.lineWidth = 4 / this.zoom;
      this.ctx.stroke();

      // Draw arrowhead for the direction of the slope
      this.drawArrowhead(endpoint, perpAngle, 16 / this.zoom);

      // Draw the slope value text
      this.ctx.save();
      this.ctx.translate(endpoint.x, endpoint.y);
      this.ctx.rotate(perpAngle);
      this.ctx.translate(0, -12 / this.zoom);
      this.ctx.textAlign = "center";
      this.ctx.font = `${12 / this.zoom}px Arial`;
      this.ctx.fillStyle = "#673AB7";
      this.ctx.fillText(`${value}°`, 0, 0);
      this.ctx.restore();
    },

    // Helper method to draw an arrowhead
    drawArrowhead(point, angle, size) {
      this.ctx.save();
      this.ctx.translate(point.x, point.y);
      this.ctx.rotate(angle);

      // Draw the arrowhead
      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.lineTo(-size, -size / 2);
      this.ctx.lineTo(-size, size / 2);
      this.ctx.closePath();

      this.ctx.fillStyle = "#673AB7";
      this.ctx.fill();
      this.ctx.restore();
    },

    // Rotate the canvas to align with a polygon's baseline
    rotateCanvasToPolygon(polygonIndex) {
      if (polygonIndex < 0 || polygonIndex >= this.polygons.length) {
        return;
      }

      const polygon = this.polygons[polygonIndex];

      // Try to use existing slope baseline if available
      if (polygon.metadata && polygon.metadata.slope) {
        const { baseLine } = polygon.metadata.slope;
        this.rotateCanvasToBaseline(
          polygon,
          baseLine.startIndex,
          baseLine.endIndex
        );
        return;
      }

      // If no slope is defined, show notification and do nothing
      this.showBaselineNotification = true;
      setTimeout(() => {
        this.showBaselineNotification = false;
      }, 3000); // Hide after 3 seconds
    },

    // Helper method to calculate and apply rotation based on a baseline
    rotateCanvasToBaseline(polygon, startIndex, endIndex) {
      const p1 = polygon[startIndex];
      const p2 = polygon[endIndex];

      // Calculate the angle of the baseline
      const baseAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);

      // Calculate the center point of the polygon
      let centerX = 0,
        centerY = 0;
      polygon.forEach((point) => {
        centerX += point.x;
        centerY += point.y;
      });
      centerX /= polygon.length;
      centerY /= polygon.length;

      // Calculate the midpoint of the baseline
      const midpointX = (p1.x + p2.x) / 2;
      const midpointY = (p1.y + p2.y) / 2;

      // Calculate a vector from the midpoint to the center
      const vecX = centerX - midpointX;
      const vecY = centerY - midpointY;

      // Calculate the perpendicular vector to the baseline
      const perpX = Math.cos(baseAngle + Math.PI / 2);
      const perpY = Math.sin(baseAngle + Math.PI / 2);

      // Dot product to determine if polygon is above or below the baseline
      const dotProduct = vecX * perpX + vecY * perpY;

      // First, rotate to make the baseline horizontal
      let targetAngle = -baseAngle;

      // Then add 180 degrees to ensure baseline is at the bottom
      targetAngle += Math.PI;

      // Calculate the required rotation
      this.canvasRotation = targetAngle;

      // Normalize the rotation angle to be between -PI and PI
      while (this.canvasRotation > Math.PI) this.canvasRotation -= 2 * Math.PI;
      while (this.canvasRotation < -Math.PI) this.canvasRotation += 2 * Math.PI;

      // Redraw with the new rotation
      this.redrawCanvas();

      // Switch back to move tool after rotation
      this.setTool("move");
    },

    // Reset canvas orientation and zoom to default
    resetCanvasOrientation() {
      // Save current position to animate from
      const originalRotation = this.canvasRotation;
      const originalZoom = this.zoom;
      const originalTransform = { ...this.viewportTransform };

      // Reset to default values
      this.canvasRotation = 0;
      this.zoom = 1;
      this.viewportTransform = { x: 0, y: 0, zoom: 1 };

      // Redraw with the new settings
      this.redrawCanvas();

      // Switch back to move tool after reset
      this.setTool("move");
    },

    createPanel(position, polygonIndex, panelModel) {
      // Check if we've reached the maximum number of panels
      if (this.totalPanels > 0 && this.panels.length >= this.totalPanels) {
        // Show notification that maximum panels reached
        this.showPanelNotification = "limit";
        setTimeout(() => {
          this.showPanelNotification = false;
        }, 3000);
        return;
      }

      // Get the polygon and its baseline
      const polygon = this.polygons[polygonIndex];
      let baseAngle = 0;

      // If the polygon has a slope defined, use its baseline
      if (polygon.metadata && polygon.metadata.slope) {
        const { baseLine } = polygon.metadata.slope;
        const p1 = polygon[baseLine.startIndex];
        const p2 = polygon[baseLine.endIndex];
        baseAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
      } else {
        // If no slope is defined, use the longest edge as the baseline
        let longestEdgeIndex = 0;
        let maxLength = 0;

        for (let i = 0; i < polygon.length; i++) {
          const nextIndex = (i + 1) % polygon.length;
          const p1 = polygon[i];
          const p2 = polygon[nextIndex];

          const length = Math.sqrt(
            Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)
          );

          if (length > maxLength) {
            maxLength = length;
            longestEdgeIndex = i;
          }
        }

        const p1 = polygon[longestEdgeIndex];
        const p2 = polygon[(longestEdgeIndex + 1) % polygon.length];
        baseAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
      }

      // Convert panel dimensions from millimeters to pixels
      const width = (panelModel.width / 1000) * (1 / this.realWorldScale); // Convert mm to m then to pixels
      const height = (panelModel.length / 1000) * (1 / this.realWorldScale); // Convert mm to m then to pixels

      // Get slope angle from polygon metadata if available
      let slopeAngle = 0;
      if (polygon.metadata && polygon.metadata.slope) {
        slopeAngle = polygon.metadata.slope.value;
      }

      // Convert slope angle to radians and calculate cos(slope)
      const slopeRad = (slopeAngle * Math.PI) / 180;
      const slopeFactor = Math.cos(slopeRad);

      // Adjust dimensions based on slope and panel orientation
      let adjustedWidth = width;
      let adjustedHeight = height;

      if (!this.isVerticalPanel) {
        // Horizontal panels: adjust height with slope
        adjustedHeight = height * slopeFactor;
      } else {
        // Vertical panels: adjust width with slope
        adjustedWidth = width * slopeFactor;
      }

      // For vertical panels, swap the visual dimensions (panel appears taller)
      if (this.isVerticalPanel) {
        [adjustedWidth, adjustedHeight] = [adjustedHeight, adjustedWidth];
      }

      // Calculate the panel points WITHOUT baseline rotation - let the drawing method handle it
      const halfWidth = adjustedWidth / 2;
      const halfHeight = adjustedHeight / 2;

      // Create panel points as a simple rectangle (rotation will be applied during drawing)
      // adjustedWidth and adjustedHeight are already swapped for vertical panels above
      const panelPoints = [
        { x: position.x - halfWidth, y: position.y - halfHeight },
        { x: position.x + halfWidth, y: position.y - halfHeight },
        { x: position.x + halfWidth, y: position.y + halfHeight },
        { x: position.x - halfWidth, y: position.y + halfHeight },
      ];

      // Calculate orientation relative to the baseline
      const orientationAngle = (baseAngle * 180) / Math.PI; // Convert radians to degrees

      // Add panel to panels array
      const newPanel = {
        id: Date.now().toString(),
        type: "panel_design",
        points: panelPoints,
        polygonIndex: polygonIndex,
        model_id: panelModel.id, // Just store the model ID for lookup in equipmentStore
        isVertical: this.isVerticalPanel,
        properties: {
          color: "#2196F3",
          fillOpacity: 0.7,
          strokeWidth: 1,
          strokeColor: "#1565C0",
          zIndex: this.panels.length + 10, // Ensure panels appear above polygons
          slope: {
            value: slopeAngle, // Slope angle in degrees
          },
          orientation: orientationAngle, // Angle in degrees relative to the baseline
        },
      };



      this.panels.push(newPanel);

      // Preload the panel image for this model
      this.loadPanelImage(panelModel);

      // Log panel points to console
      console.log("New panel created with points:", newPanel.points);
      console.log("Point 1 (Top-Left):", newPanel.points[0]);
      console.log("Point 2 (Top-Right):", newPanel.points[1]);
      console.log("Point 3 (Bottom-Right):", newPanel.points[2]);
      console.log("Point 4 (Bottom-Left):", newPanel.points[3]);

      console.log("Canvas dimensions:", this.canvasWidth, this.canvasHeight);

      this.redrawCanvas();
    },

    // Calculate panel preview properties for cursor display (OPTIMIZED with smart caching)
    calculatePanelPreview(position, polygonIndex) {
      if (!this.selectedPanelModel) return null;

      // Use smart cache to avoid expensive recalculations
      if (!this.populateSmartPanelCache(polygonIndex)) {
        return null;
      }

      // Get cached dimensions and properties
      const { dimensions, baseAngle, slopeAngle } = this.smartPanelCache;
      const { width: adjustedWidth, height: adjustedHeight } = dimensions;

      // Only calculate the 4 corner points (fast operation)
      const halfWidth = adjustedWidth / 2;
      const halfHeight = adjustedHeight / 2;

      const panelPoints = [
        { x: position.x - halfWidth, y: position.y - halfHeight },
        { x: position.x + halfWidth, y: position.y - halfHeight },
        { x: position.x + halfWidth, y: position.y + halfHeight },
        { x: position.x - halfWidth, y: position.y + halfHeight },
      ];

      return {
        points: panelPoints,
        baseAngle,
        slopeAngle,
        adjustedWidth,
        adjustedHeight,
        isVertical: this.isVerticalPanel,
        polygonIndex,
      };
    },

    // Add new method to check if a panel overlaps with any other panel
    isPanelOverlapping(panel) {
      // Define a small tolerance for the gap between panels (1px)
      const tolerance = 1 / this.zoom;

      // Check against all other panels
      return this.panels.some((otherPanel) => {
        // Skip comparing with itself
        if (otherPanel.id === panel.id) return false;

        // Skip if other panel is also a preview panel
        if (otherPanel.id === 'preview') return false;

        // Check if any point of the current panel is inside the other panel
        return panel.points.some((point) => {
          // First check if the point is inside the other panel
          const isInside = this.isPointInPolygon(
            point.x,
            point.y,
            otherPanel.points
          );

          // If the point is inside, check if it's just on the edge (within tolerance)
          if (isInside) {
            // Check if the point is near any edge of the other panel
            const isNearEdge = this.isPointNearEdge(
              point,
              otherPanel.points,
              tolerance
            );
            // Only consider it overlapping if it's not near an edge
            return !isNearEdge;
          }

          return false;
        });
      });
    },

    // Add new helper method to check if a point is near an edge
    isPointNearEdge(point, polygonPoints, tolerance) {
      // Check each edge of the polygon
      for (let i = 0; i < polygonPoints.length; i++) {
        const p1 = polygonPoints[i];
        const p2 = polygonPoints[(i + 1) % polygonPoints.length];

        // Calculate distance from point to line segment
        const distance = this.distanceToLineSegment(
          point.x,
          point.y,
          p1.x,
          p1.y,
          p2.x,
          p2.y
        );

        // If point is within tolerance of the edge, consider it "near"
        if (distance <= tolerance) {
          return true;
        }
      }

      return false;
    },


    // Add new method to check if a panel overlaps with any obstacle
    isPanelOverlappingObstacle(panel) {
      // Define a small tolerance for the gap between panels and obstacles (1px)
      const tolerance = 1 / this.zoom;

      // Check against all obstacles in the same parent polygon
      return this.obstacles.some((obstacle) => {
        // Only check obstacles in the same parent polygon as the panel
        if (obstacle.parentIndex !== panel.polygonIndex) return false;

        // Check if any point of the panel is inside the obstacle
        return panel.points.some((point) => {
          // First check if the point is inside the obstacle
          const isInside = this.isPointInPolygon(point.x, point.y, obstacle.points);

          // If the point is inside, check if it's just on the edge (within tolerance)
          if (isInside) {
            // Check if the point is near any edge of the obstacle
            const isNearEdge = this.isPointNearEdge(
              point,
              obstacle.points,
              tolerance
            );
            // Only consider it overlapping if it's not near an edge
            return !isNearEdge;
          }

          return false;
        });
      });
    },

    drawPanel(panel, index) {
      if (!this.ctx || !panel.points) return;

      // Check if panel is completely inside its parent polygon
      const isFullyInside = this.isPanelInsidePolygon(panel);
      // Check if panel overlaps with other panels
      const isOverlapping = this.isPanelOverlapping(panel);
      // Check if panel overlaps with any obstacle
      const isOverlappingObstacle = this.isPanelOverlappingObstacle(panel);

      // Check if panel is selected
      const isSelected = this.selectedPanels.includes(index);

      // Calculate the center point of the panel
      const centerX =
        panel.points.reduce((sum, p) => sum + p.x, 0) / panel.points.length;
      const centerY =
        panel.points.reduce((sum, p) => sum + p.y, 0) / panel.points.length;

      // Calculate the width and height of the panel
      const width = Math.sqrt(
        Math.pow(panel.points[1].x - panel.points[0].x, 2) +
          Math.pow(panel.points[1].y - panel.points[0].y, 2)
      );
      const height = Math.sqrt(
        Math.pow(panel.points[3].x - panel.points[0].x, 2) +
          Math.pow(panel.points[3].y - panel.points[0].y, 2)
      );

      // Get the rotation angle from panel properties (stored during creation)
      const angle = (panel.properties?.orientation || 0) * Math.PI / 180; // Convert degrees to radians

      // Save the current context state
      this.ctx.save();

      // Move to the center of the panel
      this.ctx.translate(centerX, centerY);
      this.ctx.rotate(angle);

      // Get the panel model to determine the correct image
      const equipmentStore = useEquipmentStore();
      const panelModel = equipmentStore.panels.find(p => p.id === panel.model_id);
      
      // Get the appropriate panel image based on the model color
      let panelImage = this.panelImage; // Fallback to default
      if (panelModel) {
        // Use cached image if available, otherwise use default and load in background
        const imageUrl = getPanelImageUrl(panelModel);
        panelImage = this.panelImageCache[imageUrl] || this.panelImage;
        
        // If not cached, load the image asynchronously
        if (!this.panelImageCache[imageUrl]) {
          this.loadPanelImage(panelModel);
        }
      }

      // Draw the panel image
      if (panelImage) {
        // Set opacity based on selection and validity
        let opacity;
        if (isFullyInside && !isOverlapping && !isOverlappingObstacle) {
          // Valid panel
          opacity = isSelected ? 1.0 : 0.8; // Full opacity when selected, slightly transparent when not
        } else {
          // Invalid panel
          opacity = isSelected ? 0.6 : 0.4; // More transparent to indicate invalidity
        }

        this.ctx.globalAlpha = opacity;
        this.ctx.drawImage(
          panelImage,
          -width / 2,
          -height / 2,
          width,
          height
        );
        this.ctx.globalAlpha = 1;

        // If panel is invalid, draw a red overlay
        if (!isFullyInside || isOverlapping || isOverlappingObstacle) {
          this.ctx.fillStyle = "rgba(255, 0, 0, 0.2)"; // Light red overlay for invalid panels
          this.ctx.fillRect(-width / 2, -height / 2, width, height);
        }
      }

      // Draw panel number
      this.ctx.font = `${16 / this.zoom}px Arial`;
      this.ctx.textAlign = "center";
      this.ctx.textBaseline = "middle";
      this.ctx.fillStyle = "#FFFFFF";
      this.ctx.strokeStyle = "#000000";
      this.ctx.lineWidth = 2 / this.zoom;

      // Draw text with outline for better visibility
      const panelNumber = (index + 1).toString();

      // Apply 180-degree rotation to the text only
      this.ctx.save();
      this.ctx.rotate(Math.PI);
      this.ctx.strokeText(panelNumber, 0, 0);
      this.ctx.fillText(panelNumber, 0, 0);
      this.ctx.restore();

      // If selected, draw a selection indicator
      if (isSelected) {
        // Draw selection handles at corners
        const corners = [
          { x: -width / 2, y: -height / 2 },
          { x: width / 2, y: -height / 2 },
          { x: width / 2, y: height / 2 },
          { x: -width / 2, y: height / 2 },
        ];

        for (const corner of corners) {
          this.ctx.beginPath();
          this.ctx.arc(corner.x, corner.y, 5 / this.zoom, 0, Math.PI * 2);
          this.ctx.fillStyle = "#FFC107";
          this.ctx.fill();
          this.ctx.strokeStyle = "#FFFFFF";
          this.ctx.lineWidth = 1 / this.zoom;
          this.ctx.stroke();
        }
      }

      // Restore the context state
      this.ctx.restore();
    },

    // Draw panel preview cursor for panel tool
    drawPanelPreview(previewProperties) {
      if (!this.ctx || !previewProperties || !previewProperties.points) {
        return;
      }

      const { points, adjustedWidth, adjustedHeight, isVertical, polygonIndex, baseAngle } = previewProperties;

      // Create a temporary panel object for overlap detection
      const tempPanel = {
        points: points,
        polygonIndex: polygonIndex,
        id: 'preview', // Special ID to identify preview panel
        isVertical: isVertical,
        model_id: this.selectedPanelModel,
        properties: {
          slope: { value: 0 } // Default slope for preview
        }
      };

      // Check for overlaps with existing panels and obstacles
      const isOverlapping = this.isPanelOverlapping(tempPanel);
      const isOverlappingObstacle = this.isPanelOverlappingObstacle(tempPanel);
      const isInsidePolygon = this.isPanelInsidePolygon(tempPanel);

      // Calculate the center point of the preview panel
      const centerX = points.reduce((sum, p) => sum + p.x, 0) / points.length;
      const centerY = points.reduce((sum, p) => sum + p.y, 0) / points.length;

      // Save the current context state
      this.ctx.save();

      // Move to the center of the panel and apply baseline rotation
      this.ctx.translate(centerX, centerY);
      this.ctx.rotate(baseAngle);

      // Get the panel model to determine the correct image
      const equipmentStore = useEquipmentStore();
      const panelModel = equipmentStore.panels.find(p => p.model_number === this.selectedPanelModel);

      // Get the appropriate panel image
      let panelImage = this.panelImage; // Fallback to default
      if (panelModel) {
        const imageUrl = getPanelImageUrl(panelModel);
        panelImage = this.panelImageCache[imageUrl] || this.panelImage;

        // If not cached, load the image asynchronously
        if (!this.panelImageCache[imageUrl]) {
          this.loadPanelImage(panelModel);
        }
      }

      // Draw the panel preview image with semi-transparency
      if (panelImage) {
        // Set opacity based on validity
        let opacity;
        if (isInsidePolygon && !isOverlapping && !isOverlappingObstacle) {
          // Valid preview
          opacity = 0.6; // Semi-transparent to indicate preview
        } else {
          // Invalid preview
          opacity = 0.3; // More transparent to indicate invalidity
        }

        this.ctx.globalAlpha = opacity;
        this.ctx.drawImage(
          panelImage,
          -adjustedWidth / 2,
          -adjustedHeight / 2,
          adjustedWidth,
          adjustedHeight
        );
        this.ctx.globalAlpha = 1;

        // If preview is invalid, draw a red overlay
        if (!isInsidePolygon || isOverlapping || isOverlappingObstacle) {
          this.ctx.fillStyle = "rgba(255, 0, 0, 0.3)"; // Red overlay for invalid preview
          this.ctx.fillRect(-adjustedWidth / 2, -adjustedHeight / 2, adjustedWidth, adjustedHeight);
        }
      }

      // Draw preview outline (dashed border) - color based on validity
      if (isInsidePolygon && !isOverlapping && !isOverlappingObstacle) {
        this.ctx.strokeStyle = "#4CAF50"; // Green outline for valid preview
      } else if (isOverlapping) {
        this.ctx.strokeStyle = "#F44336"; // Red outline for overlap
      } else if (isOverlappingObstacle) {
        this.ctx.strokeStyle = "#FF9800"; // Orange outline for obstacle overlap
      } else if (!isInsidePolygon) {
        this.ctx.strokeStyle = "#9C27B0"; // Purple outline for outside polygon
      } else {
        this.ctx.strokeStyle = "#2196F3"; // Blue outline for preview
      }
      
      this.ctx.lineWidth = 2 / this.zoom;
      this.ctx.setLineDash([5 / this.zoom, 3 / this.zoom]); // Dashed line
      this.ctx.strokeRect(-adjustedWidth / 2, -adjustedHeight / 2, adjustedWidth, adjustedHeight);
      this.ctx.setLineDash([]); // Reset to solid line

      // Draw the add panel icon above the preview
      const iconSize = 20 / this.zoom;
      const iconY = -adjustedHeight / 2 - 25 / this.zoom;

      // Draw the panel rectangle icon - color based on validity
      if (isInsidePolygon && !isOverlapping && !isOverlappingObstacle) {
        this.ctx.fillStyle = "#4CAF50"; // Green for valid
      } else {
        this.ctx.fillStyle = "#F44336"; // Red for invalid
      }
      this.ctx.fillRect(-iconSize / 2, iconY - iconSize / 2, iconSize, iconSize);

      // Draw the plus sign
      this.ctx.strokeStyle = "#FFFFFF";
      this.ctx.lineWidth = 2 / this.zoom;
      this.ctx.lineCap = "round";

      // Vertical line of plus
      this.ctx.beginPath();
      this.ctx.moveTo(0, iconY - iconSize / 3);
      this.ctx.lineTo(0, iconY + iconSize / 3);
      this.ctx.stroke();

      // Horizontal line of plus
      this.ctx.beginPath();
      this.ctx.moveTo(-iconSize / 3, iconY);
      this.ctx.lineTo(iconSize / 3, iconY);
      this.ctx.stroke();

      // Restore the context state
      this.ctx.restore();
    },

    // Add new method to check if a panel is fully inside its parent polygon
    isPanelInsidePolygon(panel) {
      if (
        panel.polygonIndex < 0 ||
        panel.polygonIndex >= this.polygons.length
      ) {
        return false;
      }

      const parentPolygon = this.polygons[panel.polygonIndex];

      // Check if all panel points are inside the parent polygon
      return panel.points.every((point) =>
        this.isPointInPolygon(point.x, point.y, parentPolygon)
      );
    },

    // Add new method to find the top-most panel at a point
    findTopMostPanelAt(x, y) {
      // Iterate in reverse (newest to oldest) to find the top-most panel
      for (let i = this.panels.length - 1; i >= 0; i--) {
        if (this.isPointInPolygon(x, y, this.panels[i].points)) {
          return i;
        }
      }
      return -1;
    },

    clearCanvas() {
      if (this.ctx) {
        this.ctx.fillStyle = "#f8f9fa";
        this.ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.currentPolygon = [];
        this.polygons = [];
        this.obstacles = [];
        this.panels = [];
        this.isDrawing = false;
        this.tempPoint = null;
        this.closePath = false;
        this.resetView();
        this.selectedPanels = [];
        this.deleteHistory = []; // Clear delete history
      }
    },

    // Clear only panels while keeping roofs and obstacles
    clearAllPanels() {
      // Clear panels from local state
      this.panels = [];
      this.selectedPanels = [];
      this.selectedPanelIndex = -1;
      
      // Update the project store to clear panels from drawing data
      const projectStore = useProjectStore();
      if (projectStore.project?.drawing_data) {
        const updatedDrawingData = {
          ...projectStore.project.drawing_data,
          panels: [], // Clear only panels
        };
        projectStore.setDrawingData(updatedDrawingData);
      }
      
      // Redraw canvas to reflect the changes
      this.redrawCanvas();
    },

    // Add new method to toggle panel orientation
    togglePanelOrientation() {
      this.orientationChanging = true; // Prevent mouse move handler interference
      this.isVerticalPanel = !this.isVerticalPanel;

      // Force immediate preview update if in panel tool mode
      if (this.currentTool === "addPanel") {
        // Always force a preview update when orientation changes
        this.forcePreviewUpdate = true;

        if (this.panelPreviewPosition) {
          // Recalculate preview with new orientation at current position
          const polygonIndex = this.findTopMostPolygonAt(
            this.panelPreviewPosition.x,
            this.panelPreviewPosition.y
          );

          if (polygonIndex >= 0) {
            // Invalidate smart cache to force recalculation with new orientation
            this.smartPanelCache.isValid = false;
            
            this.panelPreviewProperties = this.calculatePanelPreview(
              this.panelPreviewPosition,
              polygonIndex
            );
            this.showPanelPreview = true;
          } else {
            this.showPanelPreview = false;
            this.panelPreviewProperties = null;
          }
        }

        // Use nextTick to ensure DOM updates are complete before redrawing
        this.$nextTick(() => {
          this.scheduleRedraw();
          // Reset the flag after canvas is redrawn
          setTimeout(() => {
            this.orientationChanging = false;
          }, 100);
        });
      } else {
        this.scheduleRedraw();
        this.orientationChanging = false;
      }
    },

    handlePanelButtonClick() {
      if (this.currentTool === "addPanel") {
        this.togglePanelOrientation();
      } else {
        // Handle panel addition logic
        this.setTool("addPanel");
      }
    },













    // Add new method to handle undo
    undoDelete() {
      if (this.deleteHistory.length === 0) return;

      const lastDeletedItem = this.deleteHistory.pop();

      // Restore the deleted item based on its type
      switch (lastDeletedItem.type) {
        case "panel":
          // Ensure the panel has valid points before restoring
          if (
            lastDeletedItem.item &&
            lastDeletedItem.item.points &&
            lastDeletedItem.item.points.length > 0
          ) {
            this.panels.splice(lastDeletedItem.index, 0, lastDeletedItem.item);
          }
          break;
        case "obstacle":
          // Ensure the obstacle has valid points before restoring
          if (
            lastDeletedItem.item &&
            lastDeletedItem.item.points &&
            lastDeletedItem.item.points.length > 0
          ) {
            this.obstacles.splice(
              lastDeletedItem.index,
              0,
              lastDeletedItem.item
            );
          }
          break;
        case "polygon":
          // Ensure the polygon has valid points before restoring
          if (
            lastDeletedItem.item &&
            lastDeletedItem.item.polygon &&
            lastDeletedItem.item.polygon.length > 0
          ) {
            // Restore the polygon
            this.polygons.splice(
              lastDeletedItem.index,
              0,
              lastDeletedItem.item.polygon
            );

            // Restore any obstacles that were inside this polygon
            if (
              lastDeletedItem.item.obstacles &&
              lastDeletedItem.item.obstacles.length > 0
            ) {
              this.obstacles.push(...lastDeletedItem.item.obstacles);
            }

            // Restore any panels that were inside this polygon
            if (
              lastDeletedItem.item.panels &&
              lastDeletedItem.item.panels.length > 0
            ) {
              this.panels.push(...lastDeletedItem.item.panels);
            }
          }
          break;
      }

      this.redrawCanvas();
    },

    // Add new method to manage delete history
    addToDeleteHistory(type, index, item) {
      // Deep clone the item to ensure we have a complete copy
      let clonedItem;
      if (type === "polygon") {
        // For polygons, ensure we have a complete copy of all related data
        clonedItem = {
          polygon: JSON.parse(JSON.stringify(item.polygon)),
          obstacles: item.obstacles
            ? JSON.parse(JSON.stringify(item.obstacles))
            : [],
          panels: item.panels ? JSON.parse(JSON.stringify(item.panels)) : [],
        };
      } else {
        // For panels and obstacles, just deep clone the item
        clonedItem = JSON.parse(JSON.stringify(item));
      }

      this.deleteHistory.push({ type, index, item: clonedItem });

      // Keep history size within limit
      if (this.deleteHistory.length > this.maxHistorySize) {
        this.deleteHistory.shift();
      }
    },

    async handleCaptureDrawing() {
      try {
        const success = await captureDrawingScreenshot();
        if (success) {
          this.showCaptureNotification = true;
          setTimeout(() => {
            this.showCaptureNotification = false;
          }, 3000); // Hide after 3 seconds
        }
      } catch (error) {
        console.error("Error capturing drawing:", error);
      }
    },
    confirmClearCanvas() {
      this.clearCanvas();
      this.showClearModal = false;
    },
    cancelDeleteShape() {
      this.showDeleteShapeModal = false;
    },
    cancelDeleteRoof() {
      this.showDeleteRoofModal = false;
    },
    confirmDeleteRoof() {
      if (this.roofToDelete !== null) {
        const polygonIndex = this.roofToDelete;
        
        // Store the polygon and its related items in history before deleting
        const deletedItems = {
          polygon: this.polygons[polygonIndex],
          obstacles: this.obstacles.filter(
            (obs) => obs.parentIndex === polygonIndex
          ),
          panels: this.panels.filter(
            (panel) => panel.polygonIndex === polygonIndex
          ),
        };
        this.addToDeleteHistory("polygon", polygonIndex, deletedItems);

        // Remove the polygons and any obstacles inside it
        this.obstacles = this.obstacles.filter(
          (obs) => obs.parentIndex !== polygonIndex
        );
        this.panels = this.panels.filter(
          (panel) => panel.polygonIndex !== polygonIndex
        );
        this.polygons.splice(polygonIndex, 1);

        // Update parentIndex for remaining obstacles
        this.obstacles.forEach((obs) => {
          if (obs.parentIndex > polygonIndex) {
            obs.parentIndex--;
          }
        });

        // Update polygonIndex for remaining panels
        this.panels.forEach((panel) => {
          if (panel.polygonIndex > polygonIndex) {
            panel.polygonIndex--;
          }
        });
      }
      
      this.showDeleteRoofModal = false;
      this.roofToDelete = null;
      this.redrawCanvas();
    },
    // Add new method to populate smart panel cache with expensive calculations
    populateSmartPanelCache(polygonIndex) {
      if (!this.selectedPanelModel) return false;

      const polygon = this.polygons[polygonIndex];
      if (!polygon) return false;

      // Check if cache is already valid for current state
      if (this.smartPanelCache.isValid &&
          this.smartPanelCache.panelModelId === this.selectedPanelModel &&
          this.smartPanelCache.orientation === this.isVerticalPanel &&
          this.smartPanelCache.zoom === this.zoom &&
          this.smartPanelCache.realWorldScale === this.realWorldScale &&
          this.smartPanelCache.polygonIndex === polygonIndex) {
        return true; // Cache is valid, no need to recalculate
      }

      // Get the selected panel model (use cache if available)
      let selectedPanel = this.cachedPanelModel;
      if (!selectedPanel || selectedPanel.model_number !== this.selectedPanelModel) {
        const equipmentStore = useEquipmentStore();
        selectedPanel = equipmentStore.panels.find(
          (p) => p.model_number === this.selectedPanelModel
        );
        this.cachedPanelModel = selectedPanel; // Cache for future use
      }

      if (!selectedPanel) return false;

      // Calculate baseline angle (expensive operation)
      let baseAngle = 0;
      if (polygon.metadata && polygon.metadata.slope) {
        const { baseLine } = polygon.metadata.slope;
        const p1 = polygon[baseLine.startIndex];
        const p2 = polygon[baseLine.endIndex];
        baseAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
      } else {
        // If no slope is defined, use the longest edge as the baseline
        let longestEdgeIndex = 0;
        let maxLength = 0;

        for (let i = 0; i < polygon.length; i++) {
          const nextIndex = (i + 1) % polygon.length;
          const p1 = polygon[i];
          const p2 = polygon[nextIndex];

          const length = Math.sqrt(
            Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2)
          );

          if (length > maxLength) {
            maxLength = length;
            longestEdgeIndex = i;
          }
        }

        const p1 = polygon[longestEdgeIndex];
        const p2 = polygon[(longestEdgeIndex + 1) % polygon.length];
        baseAngle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
      }

      // Get slope angle from polygon metadata if available
      let slopeAngle = 0;
      if (polygon.metadata && polygon.metadata.slope) {
        slopeAngle = polygon.metadata.slope.value;
      }

      // Convert slope angle to radians and calculate cos(slope)
      const slopeRad = (slopeAngle * Math.PI) / 180;
      const slopeFactor = Math.cos(slopeRad);

      // Convert panel dimensions from millimeters to pixels
      const width = (selectedPanel.width / 1000) * (1 / this.realWorldScale);
      const height = (selectedPanel.length / 1000) * (1 / this.realWorldScale);

      // Adjust dimensions based on slope and panel orientation
      let adjustedWidth = width;
      let adjustedHeight = height;

      if (!this.isVerticalPanel) {
        // Horizontal panels: adjust height with slope
        adjustedHeight = height * slopeFactor;
      } else {
        // Vertical panels: adjust width with slope
        adjustedWidth = width * slopeFactor;
      }

      // For vertical panels, swap the visual dimensions (panel appears taller)
      if (this.isVerticalPanel) {
        [adjustedWidth, adjustedHeight] = [adjustedHeight, adjustedWidth];
      }

      // Populate the smart cache with all expensive calculations
      this.smartPanelCache = {
        panelModelId: this.selectedPanelModel,
        orientation: this.isVerticalPanel,
        zoom: this.zoom,
        realWorldScale: this.realWorldScale,
        polygonIndex: polygonIndex,
        dimensions: { width: adjustedWidth, height: adjustedHeight },
        baseAngle: baseAngle,
        slopeAngle: slopeAngle,
        slopeFactor: slopeFactor,
        isValid: true
      };

      return true;
    },

    // Schedule a canvas redraw using requestAnimationFrame to batch multiple requests
    scheduleRedraw() {
      if (this.pendingRedraw) {
        return; // Redraw already scheduled
      }
      
      this.pendingRedraw = true;
      
      // Cancel any existing animation frame
      if (this.redrawAnimationFrame) {
        cancelAnimationFrame(this.redrawAnimationFrame);
      }
      
      // Schedule redraw for next frame
      this.redrawAnimationFrame = requestAnimationFrame(() => {
        this.redrawCanvas();
        this.pendingRedraw = false;
        this.redrawAnimationFrame = null;
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/styles/drawing.scss";
</style>
